import { ScopedPaletteResponseEntry } from '../models';
import { SCOPE_KEY_PREFIX, SCOPE_LEVELS_ORDERED, ScopeLevel, getSelectorRepetition, levelOfEntry } from './levels';

export function buildScopeKey(level: ScopeLevel, id: number): string {
  return `${SCOPE_KEY_PREFIX}-${level}-${id}`;
}

export function scopeKeyForEntry(entry: ScopedPaletteResponseEntry): string | undefined {
  const descriptor = levelOfEntry(entry);
  if (!descriptor) return undefined;
  const id = entry[descriptor.entryIdField];
  return id != null ? buildScopeKey(descriptor.level, id) : undefined;
}

export function getScopeLevel(scopeKey: string): ScopeLevel | undefined {
  return SCOPE_LEVELS_ORDERED.find((d) => scopeKey.startsWith(`${SCOPE_KEY_PREFIX}-${d.level}-`))?.level;
}

export function parseScopeKey(scopeKey: string): { level: ScopeLevel; id: number } | undefined {
  const level = getScopeLevel(scopeKey);
  if (!level) return undefined;
  const id = Number(scopeKey.slice(`${SCOPE_KEY_PREFIX}-${level}-`.length));
  return Number.isFinite(id) ? { level, id } : undefined;
}

// Builds a CSS selector whose class is repeated for specificity. Repetition
// counts: method ×1, eduContentBook ×2, learningDomain ×(3 + 2·depth),
// eduContentTOC ×(4 + 2·depth). The directive applies the plain class once;
// the cascade uses the repeated selector so a deeper toc outranks a shallower
// toc, an LD outranks a shallower toc but loses to its own toc, and so on
// down to book and method.
//
// `depth` is the toc's depth in the content tree (or, for an LD, the depth of
// the deepest toc the LD is linked to within the loaded chain). Method/book
// ignore depth. Defaults to 0, which preserves the legacy single-toc cascade.
export function buildSelectorForScopeKey(scopeKey: string, depth = 0): string | undefined {
  const level = getScopeLevel(scopeKey);
  if (!level) return undefined;
  return `.${scopeKey}`.repeat(getSelectorRepetition(level, depth));
}
